




































































































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate-3';

import { AddressFieldData } from '@/models/select-dropdown';
import { IBusinessType } from '@/models/business';

import { setFocusOnFirstInput } from '@/utils/setFocusOnFirstInput';
import { normalizeLocationName } from '@/utils/businessLocationHelper';
import SignUpService from '@/api/SingupService';
import { isExternalOauthSession } from '@/router/router-utils';

import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import Input from '@/componentsV2/molecules/Inputs/TextField.vue';
import GoogleAddressFieldDropdown from '@/componentsV2/organisms/GoogleAddressFieldDropdown/GoogleAddressFieldDropdown.vue';
import PhoneInput from '@/components/molecules/PhoneInput/PhoneInput.vue';
import Categories from '@/components/organisms/CategoriesV2/Categories.vue';
import InputWrapper from '@/componentsV2/molecules/Inputs/InputWrapper.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';

import CloseModalIcon from '@/components/icons/CloseModal.vue';
import TwismLogoIcon from '@/components/icons/TwismLogo.vue';
import { BusinessOnboardingState } from '@/models/business-onboarding';

interface ICategory {
  name: string;
  id: string;
}

export type BusinessDetailsFormData = {
  address: AddressFieldData | null;
  aditionalAddress: string;
  businessName: string;
  locationName: string;
  website: string;
  phone: {
    code: string;
    iso: string;
    number: string;
  };
};

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    Input,
    PhoneInput,
    FilledButton,
    Categories,
    InputWrapper,
    CloseModalIcon,
    TwismLogoIcon,
    Typography,
    GoogleAddressFieldDropdown
  },
  $_veeValidate: { validator: 'new' }
})
export default class BusinessDetailsForm extends Vue {
  @Prop() defaultFormData?: BusinessDetailsFormData;
  @Prop({ default: false }) isFromProviderConnection?: boolean;

  signUpService = new SignUpService();

  loading = false;

  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  selectedCategory = this.businessCategoryForm.selectedCategory
    ? { ...this.businessCategoryForm.selectedCategory }
    : {
        id: '',
        otherValue: ''
      };

  categories: any[] = this.filledCategories;
  categoryEmptyError: string[] = [];
  categoryErrors: string[] = [];

  form: BusinessDetailsFormData = this.defaultFormData
    ? { ...this.defaultFormData }
    : {
        address: null,
        aditionalAddress: '',
        businessName: '',
        locationName: '',
        website: '',
        phone: {
          code: this.selectedCountry.phoneCode,
          iso: this.selectedCountry.countryIso2,
          number: ''
        }
      };

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get title() {
    if (this.needsToComplete) return 'Complete your business info';
    return 'Editing your business info';
  }

  get businessCategoryForm() {
    return this.$store.getters[
      'businessOnboarding/getYourBusinessCategoryForm'
    ];
  }

  get needsToComplete() {
    return this.$route.path.includes('complete');
  }

  get filledCategories() {
    return this.$store.getters['businessOnboarding/getCategories'] || [];
  }

  get otherCategory() {
    const category = this.categories.find(categ =>
      categ.label.includes('Other')
    );

    if (!category) return {};

    return category;
  }

  get hasOtherError() {
    return (
      this.otherCategory &&
      this.otherCategory.value === this.selectedCategory.id &&
      !this.selectedCategory.otherValue
    );
  }

  get initialAddressValue() {
    if (!this.form.address) {
      return {
        label: null,
        value: null
      };
    }

    const addressData = this.form.address;

    return {
      label: addressData.fullAddress,
      value: addressData.fullAddress,
      additionalInformation: addressData
    };
  }

  get yourStoreFormData() {
    return this.$store.getters['businessOnboarding/getYourStoreForm'];
  }

  get externalOauthSession() {
    return isExternalOauthSession();
  }

  get isPhysicalBusiness() {
    return (
      this.yourStoreFormData.operationType === IBusinessType.BOTH ||
      this.yourStoreFormData.operationType === IBusinessType.INSTORE
    );
  }

  get isOnlineBusiness() {
    return (
      this.yourStoreFormData.operationType === IBusinessType.BOTH ||
      this.yourStoreFormData.operationType === IBusinessType.ONLINE
    );
  }

  get selectedCountry() {
    return this.$store.getters['businessOnboarding/getSelectedCountry'];
  }

  get isValidCategory() {
    return Boolean(this.selectedCategory.id);
  }

  get businessOnboardingState(): BusinessOnboardingState {
    return this.$store.getters[
      'businessOnboarding/getBusinessOnboardingState'
    ] as BusinessOnboardingState;
  }

  goBack() {
    this.$emit('back');
  }

  async handlePreValidateBusiness() {
    try {
      this.loading = true;

      const payload = {
        business: {
          name: ' ',
          address: (this.form.address as any).address,
          city: (this.form.address as any).cityName,
          state: (this.form.address as any).stateCode,
          zip: (this.form.address as any).postCode,
          stateName: (this.form.address as any).stateName,
          fullAddress: (this.form.address as any).fullAddress,
          countryIso2: this.yourStoreFormData.country.countryIso2
        }
      };

      if (this.form.aditionalAddress)
        (payload.business as any).addressAdditional = this.form.aditionalAddress;

      const data = (await this.signUpService.preValidate(payload)) as any;

      if (data.status === 200) {
        return true;
      }

      const { response } = data;

      const errorData = response.data;

      if (
        errorData.slug &&
        errorData.slug === 'exceptions.business.theSameAddress'
      ) {
        return false;
      }
    } finally {
      this.loading = false;
    }
  }

  toggleCategory(categoryItem: { value: string; label: string }) {
    const category = this.categories.find(
      category => category.value === categoryItem.value
    );

    if (!category) return;

    this.selectedCategory.id = categoryItem.value;
    this.selectedCategory.name = categoryItem.label;
  }

  handleChangeOtherInput(value: string) {
    this.selectedCategory.otherValue = value;
  }

  handleCloseCategory() {
    this.selectedCategory = {
      id: '',
      otherValue: ''
    };
  }

  initBusinessCategories(categories?: ICategory[]) {
    if (!Array.isArray(categories)) return [];

    const formatedCategories = categories.map((item: ICategory) => ({
      value: item.id,
      label: item.name
    }));

    return formatedCategories;
  }

  @Watch('selectedCategory', { deep: true })
  removeErrors() {
    this.categoryErrors = [];
    this.categoryEmptyError = [];
  }

  @Watch('selectedCategory.otherValue')
  validate() {
    const newErrors = [];

    if (this.hasOtherError) {
      newErrors.push(this.otherCategory.value);
    }

    this.categoryErrors = newErrors;

    return newErrors.length === 0;
  }

  async handleAddressChange(addressData: { description: string; id: string }) {
    const { data } = await this.signUpService.getGoogleAdressesDetail(
      addressData.id
    );
    this.form = Object.assign({}, this.form, {
      address: data.location,
      locationName: normalizeLocationName(data.location.address)
    });
  }

  handleClearAddress() {
    this.form = Object.assign({}, this.form, {
      address: null,
      locationName: ''
    });
  }

  async onSubmit() {
    const formRef = this.$refs.observer;
    const formIsValid = await formRef.validate();

    if (!formIsValid) {
      setFocusOnFirstInput('input[aria-invalid="true"]');
    }

    if (!this.isValidCategory) {
      this.categoryEmptyError.push(this.$t(
        'common.validations.fields.category.required'
      ) as string);

      return;
    }

    if (this.selectedCategory.name.includes('Other')) {
      if (!this.selectedCategory.otherValue.trim()) {
        this.categoryErrors.push(this.otherCategory.value);
        setFocusOnFirstInput('input[aria-invalid="true"]');

        return;
      }
    } else {
      this.selectedCategory.otherValue = '';
    }
    const response = await this.handlePreValidateBusiness();

    if (!response) {
      formRef.setErrors({
        aditionalAddress: [
          'Address is being used, try to add an additional address.'
        ]
      });
      return;
    }

    if (!formIsValid) return;

    this.$store.dispatch(
      'businessOnboarding/SET_SELECTED_CATEGORY',
      this.selectedCategory
    );

    this.$emit('submit', this.form);
  }

  mounted() {
    if (!this.form.address) {
      setFocusOnFirstInput();
    }
  }
}
